import { useState } from "react"
import { format } from "date-fns"
import {
	IconEnvelopeCheckOutline18 as MailCheckIcon,
	IconTrashOutline18 as TrashIcon,
} from "nucleo-ui-outline-18"

import type { AllowedEmailDomain } from "@productlane/zero"
import { AlertDialog, IconButton, Pill } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisVerticalIcon as MoreVertical } from "@productlane/ui/src/icons/project/ellipsis-icon"

import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"

export function AllowedDomainItem({
	allowedDomain,
	onVerify,
}: {
	allowedDomain: AllowedEmailDomain
	onVerify: (data: Omit<AllowedEmailDomain, "hashedVerificationToken">) => void
}) {
	const ctx = api.useUtils()

	const [deleteDomainData, setDeleteDomainData] = useState<Omit<
		AllowedEmailDomain,
		"hashedVerificationToken"
	> | null>(null)

	const deleteAllowedEmailDomainMutation =
		api.workspace.deleteAllowedEmailDomain.useMutation({
			onSuccess: async () => {
				await ctx.workspace.getAllowedEmailDomains.invalidate()
				toast.success({
					description: "Successfully removed allowed email domain!",
					title: "Allowed email domain removed",
				})
			},
		})

	return (
		<>
			<div
				key={allowedDomain.id}
				className="group -mx-2 flex items-center justify-between rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-900"
			>
				<div className="min-w-0 flex-1">
					<div className="flex items-end gap-2 truncate text-sm font-medium">
						{allowedDomain.domain}
						<Pill
							variant={allowedDomain.isVerified ? "green" : "dark-gray"}
							className="capitalize"
							size="sm"
						>
							{allowedDomain.isVerified ? "Verified" : "Pending"}
						</Pill>
					</div>
					<div className="truncate text-xs text-secondary">
						Verification sent to {allowedDomain.verificationEmail}
					</div>
					<div className="text-xs text-secondary sm:hidden">
						{allowedDomain.isVerified && allowedDomain.verifiedAt ? (
							<>
								Verified on{" "}
								{format(new Date(allowedDomain.verifiedAt), "MMM d, yyyy")}
							</>
						) : (
							<>
								Added on{" "}
								{format(new Date(allowedDomain.createdAt), "MMM d, yyyy")}
							</>
						)}
					</div>
				</div>
				<div className="ml-4 flex shrink-0 items-center gap-1">
					{allowedDomain.isVerified && allowedDomain.verifiedAt ? (
						<div className="hidden text-xs text-secondary sm:block">
							Verified on{" "}
							{format(new Date(allowedDomain.verifiedAt), "MMM d, yyyy")}
						</div>
					) : (
						<div className="hidden text-xs text-secondary sm:block">
							Added on{" "}
							{format(new Date(allowedDomain.createdAt), "MMM d, yyyy")}
						</div>
					)}
					<RoleCheck only={["ADMIN"]}>
						<DropdownMenu
							trigger={
								<IconButton
									Icon={MoreVertical}
									variant="dark-ghost"
									size="md"
									iconClassName="fill-current"
								/>
							}
							contentProps={{ side: "bottom", align: "end" }}
						>
							<DropdownMenuGroup>
								{!allowedDomain.isVerified && (
									<DropdownMenuItem
										Icon={MailCheckIcon}
										onClick={() => onVerify(allowedDomain)}
									>
										Verify domain
									</DropdownMenuItem>
								)}
								<DropdownMenuItem
									onClick={() => setDeleteDomainData(allowedDomain)}
									Icon={TrashIcon}
								>
									Remove domain
								</DropdownMenuItem>
							</DropdownMenuGroup>
						</DropdownMenu>
					</RoleCheck>
				</div>
			</div>

			<AlertDialog
				isOpen={!!deleteDomainData}
				onOpenChange={(isOpen) => {
					if (!isOpen) {
						setDeleteDomainData(null)
					}
				}}
				confirmText="Remove domain"
				title="Are you sure?"
				description={
					<div>
						Are you sure you want to delete the allowed domain{" "}
						<strong>{deleteDomainData?.domain}</strong>?<br />
						<br />
						After you remove it, anyone with an email ending in{" "}
						{deleteDomainData?.domain} needs an invite to join.
					</div>
				}
				onCancel={() => {
					setDeleteDomainData(null)
					analytics.track("delete-allowed-domain-canceled")
				}}
				onConfirm={async () => {
					analytics.track("delete-allowed-domain-confirmed")

					await deleteAllowedEmailDomainMutation.mutateAsync({
						id: deleteDomainData?.id || "",
					})
				}}
			/>
		</>
	)
}
